import type { ReactNode } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { gaps } from "../../../../components/primitives/spacing";
import type { UseSubmitUrlsResult } from "./useSubmitUrls";
import { classifyUrls } from "./sourceConnectorSupport";
import type { SubmitUrlResult, SupportedSourceConnector } from "../../../../lib/corpusApi";
import { RejectionFindingSummary } from "../../../../components/domain/data-display/RejectionFindingSummary";

interface Props {
  open: boolean;
  onClose: () => void;
  submission: UseSubmitUrlsResult;
  /** Called after a batch finishes, so the caller can refresh the document table (in case
   *  anything finalizes near-instantly). */
  onSubmitted: () => void;
  /** From useExtractionWarmup. While false, submitting is disabled — the extraction backend can
   *  be cold (Modal free-tier scale-to-zero), and a submission right now risks outliving
   *  corpus's own CPU budget. The URL field stays usable so a curator can paste in the meantime. */
  extractionWarm: boolean;
  /** From useExtractionWarmup. Null until the manifest has loaded — no warning is shown in that
   *  window rather than a false one. Used to warn before submitting a URL that either matches
   *  no connector (will be rejected outright — there is no generic fallback) or matches a
   *  best-effort connector (may still fail extraction, e.g. a PubMed URL with no PMC copy). */
  sourceConnectors: SupportedSourceConnector[] | null;
}

const RESULT_DISPLAY: Record<
  SubmitUrlResult["state"],
  { severity: "success" | "error" | "info" | "warning"; icon: ReactNode; label: string }
> = {
  submitting: { severity: "info", icon: <CircularProgress size={16} />, label: "Submitting…" },
  submitted: {
    severity: "success",
    icon: <CheckCircleOutlineIcon />,
    label: "Submitted — track progress in the panel at the bottom left.",
  },
  duplicate: {
    severity: "info",
    icon: <InfoOutlinedIcon />,
    label: "Already in corpus — nothing to do.",
  },
  rejected: {
    severity: "warning",
    icon: <WarningAmberIcon />,
    label: "Rejected at corpus admission gate.",
  },
  error: { severity: "error", icon: <ErrorOutlineIcon />, label: "" },
};

/** Modal form for submitting one or more URLs, reached via the "Submit URLs" button in the
 *  header of Documents. Only covers the submit call itself (fast — spawns a Tangrowth job without
 *  waiting on it); once submitted, the server finishes the job without this dialog. */
export default function SubmitUrlsDialog({
  open,
  onClose,
  submission,
  onSubmitted,
  extractionWarm,
  sourceConnectors,
}: Props) {
  const isSubmitting = submission.progress !== null && !submission.progress.done;
  const isSubmitDone = submission.progress?.done === true;
  const submittedCount = submission.results.filter((r) => r.state === "submitted").length;
  const duplicateCount = submission.results.filter((r) => r.state === "duplicate").length;
  const rejectedCount = submission.results.filter((r) => r.state === "rejected").length;
  const failCount = submission.results.filter((r) => r.state === "error").length;
  const okCount = submittedCount + duplicateCount;

  const pastedUrls = submission.urlInput
    .split("\n")
    .map((u) => u.trim())
    .filter(Boolean);
  const { unmatched: unmatchedUrls, bestEffort: bestEffortUrls } = classifyUrls(
    pastedUrls,
    sourceConnectors
  );

  async function onSubmit(e: React.FormEvent) {
    await submission.handleSubmit(e);
    onSubmitted();
  }

  return (
    <Dialog open={open} onClose={isSubmitting ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Submit URLs</DialogTitle>
      <DialogContent>
        <Stack gap={gaps.base} sx={{ pt: 0.5 }}>
          {!extractionWarm && (
            <Alert severity="info">
              Kodakku is still starting up — this should only take a few seconds. Submitting is
              disabled until it's ready.
            </Alert>
          )}
          <Stack component="form" onSubmit={onSubmit} gap={gaps.base}>
            <TextField
              multiline
              minRows={3}
              maxRows={10}
              fullWidth
              autoFocus
              placeholder={"https://example.com/study-1\nhttps://example.com/study-2"}
              helperText="Paste multiple URLs, one per line — each is submitted and tracked independently."
              value={submission.urlInput}
              onChange={(e) => submission.setUrlInput(e.target.value)}
              disabled={isSubmitting}
              inputProps={{ style: { fontFamily: "monospace", fontSize: 13 } }}
            />
            {unmatchedUrls.length > 0 && (
              <Alert severity="error">
                <Typography variant="body2">
                  {unmatchedUrls.length === 1
                    ? "1 URL isn't supported"
                    : `${unmatchedUrls.length} URLs aren't supported`}{" "}
                  and will be rejected — the document service has no generic fallback. If this is a
                  new publisher or provider, contact engineering.
                </Typography>
                <Stack sx={{ mt: 0.5 }}>
                  {unmatchedUrls.map((url) => (
                    <Typography
                      key={url}
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontFamily: "monospace", wordBreak: "break-all" }}
                    >
                      {url}
                    </Typography>
                  ))}
                </Stack>
              </Alert>
            )}
            {bestEffortUrls.length > 0 && (
              <Alert severity="warning">
                <Typography variant="body2">
                  {bestEffortUrls.length === 1
                    ? "1 URL isn't guaranteed to extract"
                    : `${bestEffortUrls.length} URLs aren't guaranteed to extract`}{" "}
                  — submission may fail if no full text is available.
                </Typography>
                <Stack sx={{ mt: 0.5 }}>
                  {bestEffortUrls.map((url) => (
                    <Typography
                      key={url}
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontFamily: "monospace", wordBreak: "break-all" }}
                    >
                      {url}
                    </Typography>
                  ))}
                </Stack>
              </Alert>
            )}
            <Stack direction="row" gap={gaps.base} sx={{ alignItems: "center" }}>
              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting || !extractionWarm || !submission.urlInput.trim()}
              >
                {isSubmitting ? "Submitting…" : "Submit"}
              </Button>
              {isSubmitting && <CircularProgress size={20} />}
            </Stack>
          </Stack>

          {isSubmitting && submission.progress && (
            <Stack gap={gaps.snug}>
              <LinearProgress
                variant="determinate"
                value={(submission.progress.completed / submission.progress.total) * 100}
              />
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                {submission.progress.completed}/{submission.progress.total} settled
              </Typography>
            </Stack>
          )}

          {isSubmitDone && (
            <Alert
              severity={
                failCount === 0 && rejectedCount === 0
                  ? "success"
                  : failCount === 0
                    ? "warning"
                    : okCount > 0
                      ? "warning"
                      : "error"
              }
            >
              {[
                submittedCount > 0 ? `${submittedCount} submitted` : null,
                duplicateCount > 0 ? `${duplicateCount} already in corpus` : null,
                rejectedCount > 0 ? `${rejectedCount} rejected at admission` : null,
                failCount > 0 ? `${failCount} failed to submit` : null,
              ]
                .filter(Boolean)
                .join(", ")}
              {submittedCount > 0 && failCount === 0 && rejectedCount === 0
                ? " — track progress in the panel at the bottom left."
                : "."}
            </Alert>
          )}

          {submission.results.length > 0 && (
            <Stack gap={gaps.snug}>
              {submission.results.map((r, i) => {
                const display = RESULT_DISPLAY[r.state];
                return (
                  <Alert
                    key={i}
                    severity={display.severity}
                    icon={display.icon}
                    sx={{ alignItems: "flex-start" }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: "monospace", wordBreak: "break-all" }}
                    >
                      {r.url}
                    </Typography>
                    {display.label && (
                      <Typography variant="caption" color="text.secondary">
                        {display.label}
                      </Typography>
                    )}
                    {r.state === "error" && (
                      <Typography variant="caption" color="error.dark">
                        {r.error}
                      </Typography>
                    )}
                    {r.state === "rejected" && (
                      <RejectionFindingSummary
                        code={r.rejection.code}
                        message={r.rejection.message}
                      />
                    )}
                  </Alert>
                );
              })}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
